/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { restartableTask, timeout } from 'ember-concurrency';
import { TYPING_DEBOUNCE_MS } from 'volta/components/bv-app-search';
import { parseCFApiParamKey } from 'volta/models/custom-field-definition';
import { scalarEquals } from 'volta/utils/array-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { DynamicDatePrefix } from "volta/models/constants/date-filters";

import type { TComparatorOption } from '../comparator-select';
import type { IResourceName } from 'volta/models/base-model';
import type { IntlService } from 'ember-intl';
import type LabelService from 'volta/services/label-service';

interface FiltersBaseFilterSignature {
  Args: {
    filterValues?: TFilterValues;
    allowClear?: boolean;
    resourceName: IResourceName;
    initialValue?: TFilterValue;
    definition: TFilterDefinition;
    onChange: (filterValue: TFilterValue, field: string) => void;
    clearDefaultValue?: string[];
  };
}

export default class FiltersBaseFilterComponent extends Component<FiltersBaseFilterSignature> {
  /**
   * Services
   */

  @service intl!: IntlService;
  @service labelService!: LabelService;

  /**
   * Tracked properties
   */

  @tracked enabled: boolean = false;
  @tracked values: string[] = [];
  @tracked comparator?: TComparator;

  /**
   * Getters
   */

  get hasSingleComparator() {
    if (!this.args.definition) {
      return true;
    }
    return this.args.definition.comparators.length < 2;
  }

  get isMultipleComparator() {
    return this.checkIsMultipleComparator(this.comparator);
  }

  get firstComparator() {
    const { comparators } = this.args.definition ?? {};
    if (!comparators) {
      return undefined;
    }
    return comparators.length > 0 ? comparators[0] : undefined;
  }

  @cached
  get title() {
    const { customFieldDefinition, queryParam, title } = this.args.definition ?? {};
    const labelKey = customFieldDefinition ? parseCFApiParamKey(queryParam) : queryParam;
    return customFieldDefinition
      ? this.labelService.tOrElse(labelKey, title)
      : title ??
          this.labelService.tOrElse(
            (queryParam && 'filterDefinitions.' + queryParam) as string,
            this.intl.t(queryParam!)
          );
  }

  /**
   * Actions
   */

  @action
  handleInitialValueChange(_element: HTMLElement, [initialValue]: [TFilterValue | undefined]) {
    this.initValues(initialValue);
  }

  valuesDidChange = restartableTask(async (values?: string[]) => {
    this.enableClear(values);
    this.values = values ?? [];
    const filterValue = this.buildFilterValue();

    if (filterValue) {
      await timeout(TYPING_DEBOUNCE_MS);
      this.args.onChange?.(filterValue, this.args.definition.queryParam);
    }
  });

  @action
  selectComparator(newComparator: TComparatorOption) {
    const oldComparator = this.comparator;

    if (oldComparator !== newComparator.key) {
      this.checkDynamicFilter(newComparator);
      const wasMultiple = this.isMultipleComparator;
      const isMultiple = this.checkIsMultipleComparator(newComparator.key);

      // Only keep the first value if we're passing
      // from multiple to single value
      if (wasMultiple && !isMultiple) {
        this.values = this.values.slice(0, 1);
      }

      // Make sure bt filter has 2 values
      if (newComparator.key === 'bt' && this.values.length === 1) {
        this.values = [this.values[0]!, this.values[0]!];
      }

      this.comparator = newComparator.key;
      const filterValue = this.buildFilterValue();
      if (filterValue) {
        this.args.onChange?.(filterValue, this.args.definition.queryParam);
      }
    }
  }

  private checkDynamicFilter(newComparator?: TComparatorOption) {
    if (this.values[0]?.startsWith(DynamicDatePrefix) && newComparator?.key == 'bt') {
      this.values = [];
    }
  }

  @action
  initValues(initialValue?: TFilterValue) {
    this.enableClear(initialValue?.values);
    this.values = (initialValue?.values ?? []) as string[];
    this.comparator = initialValue?.comparator ?? this.comparator ?? this.firstComparator;
  }

  /**
   * Private utils
   */

  private checkIsMultipleComparator(comparator?: TComparator) {
    return comparator && ['in', 'ni', 'all', 'any', 'bt'].includes(comparator);
  }

  private buildFilterValue(): TFilterValue | undefined {
    const comparator = this.comparator ?? this.firstComparator;
    return comparator
      ? {
          comparator,
          values: this.values,
          enabled: this.enabled
        }
      : undefined;
  }

  private enableClear(values?: string[]): void {
    const { clearDefaultValue } = this.args;
    if (clearDefaultValue) {
      this.enabled = !scalarEquals(values, clearDefaultValue);
    } else {
      this.enabled = values ? values.length > 0 : false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::BaseFilter': typeof FiltersBaseFilterComponent;
    'filters/base-filter': typeof FiltersBaseFilterComponent;
  }
}
